// Shared trade-card parts (2026-10-14) so the BUY and SELL options cards look and behave the same:
// one header layout, one button order (Start / Delete | Settings · Stop entry · Square off · Details), the same
// summary cells (bold labels), a collapse toggle remembered per trade for the browser session, and an alerts strip
// that is ALWAYS shown - collapsing a card never hides a critical warning. Display only.
import React, { useState } from "react";
import { Maximize2 } from "lucide-react";
import { progressText } from "@/lib/marks";

const bidaskTone = (v) => (v == null || Number(v) === 0 || Number.isNaN(Number(v)) ? "text-[var(--term-secondary)]"
  : Number(v) > 0 ? "text-[#00C805]" : "text-[#FF3B30]");

export const BTN = "h-7 px-2.5 rounded-sm text-xs font-bold border border-[var(--term-border-strong)] hover:bg-[var(--term-hover)] disabled:opacity-40";

export function Pill({ cls, children, testId, title }) {
  return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm whitespace-nowrap ${cls || "bg-[var(--term-s3)]"}`} data-testid={testId} title={title}>{children}</span>;
}

/** One summary cell: a small but BOLD uppercase label over a mono value. With `onEdit` it opens trade settings. */
export function Stat({ label, children, testId, title, onEdit, big }) {
  const inner = <>
    <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--term-secondary)] whitespace-nowrap">{label}{onEdit ? " ✎" : ""}</span>
    <span className={`mono font-semibold leading-tight break-words ${big ? "text-base" : "text-[12px]"}`} data-testid={onEdit ? undefined : testId}>{children}</span>
  </>;
  if (onEdit) {
    return <button type="button" onClick={onEdit} title={title || "Edit in trade settings"} data-testid={testId}
      className="flex flex-col text-left rounded-sm px-1 -mx-1 hover:bg-[var(--term-hover)]">{inner}</button>;
  }
  return <div className="flex flex-col min-w-0" title={title}>{inner}</div>;
}

/** The summary row: aligned cells that wrap cleanly on small screens. */
export function StatGrid({ children, testId }) {
  return <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 xl:grid-cols-8 gap-x-5 gap-y-2 text-xs" data-testid={testId}>{children}</div>;
}

/** Whether the trade's Details modal is open (30 Oct 2026: details open in a modal, so nothing is remembered across
 *  visits; a highlighted trade - reached from a link - opens with its details showing). */
export function useCardExpanded(id, initial) {
  return useState(!!initial);
}

export function CardToggle({ expanded, onToggle }) {
  return (
    <button type="button" onClick={onToggle} aria-expanded={expanded} data-testid="hedge-card-toggle" title="Open full trade details"
      className="inline-flex h-6 w-6 items-center justify-center rounded border border-[var(--term-border)] text-[var(--term-secondary)] hover:bg-[var(--term-hover)]">
      <Maximize2 className="h-3 w-3" aria-hidden="true" /></button>
  );
}

/** The card's buttons: Start, Delete (drafts) | Settings, Stop entry, Details || Square off. Square off is LAST and set
 *  apart (UI/UX audit Phase 4): it sat between Settings and Details, one slip away from a routine click. */
export function CardActions({ start, del, settings, stopEntry, squareOff, details }) {
  return (
    <span className="ml-auto flex items-center gap-1 flex-wrap justify-end" data-testid="hedge-actions">
      {start}{del}{settings}{stopEntry}{details}
      {squareOff && <span className="ml-2 pl-2 border-l border-[var(--term-border-strong)] flex" data-testid="hedge-actions-exit">{squareOff}</span>}
    </span>
  );
}

/** The square-off question with what it would realise now: "… — about −₹11,996 at the bid now." */
export function squareOffText(base, executable, priceState, basis, money) {
  const est = executable == null ? "" : ` Closing now realises about ${money(executable)} (net, ${basis}).`;
  const closed = priceState === "MARKET_CLOSED" ? " The market is closed: the orders wait for the session and may fill at different prices." : "";
  return `${base}${est}${closed}`;
}

/** Critical warnings, shown collapsed or expanded. `alerts` = [{key, text, tone: "bad" | "warn"}]. */
export function AlertStrip({ alerts, testId = "hedge-alerts" }) {
  if (!alerts?.length) return null;
  return (
    <div className="space-y-1" data-testid={testId}>
      {alerts.map((a) => (
        <div key={a.key} data-testid={`${testId}-${a.key}`} title={a.title}
          className={`text-xs font-semibold rounded-sm border px-2 py-1 ${a.tone === "bad" ? "border-[#FF3B30]/60 bg-[#FF3B30]/10 text-[#FF3B30]" : "border-[#B45309]/50 bg-[#B45309]/10 text-[#B45309]"}`}>
          ⚠ {a.text}</div>))}
    </div>
  );
}

/** SELL card warnings that must never hide behind a collapsed card. */
export function sellAlerts(t, pairs, { bandsLeft = 0, depthShort = false, bandsLeftAuto = 0, nearTrigger = [], reached = [], closing = [] } = {}) {
  const out = [];
  if (t.recovery_state && t.recovery_state !== "ACKNOWLEDGED") out.push({ key: "recovery", tone: "bad", text: `Needs attention: restart recovery ${t.recovery_state}` });
  for (const p of pairs || []) {
    if (p.uncovered) out.push({ key: `uncovered-${p.pair_no}`, tone: "bad", text: `Pair ${p.pair_no}: UNCOVERED ${p.uncovered.short_side} ${p.uncovered.qty} — one short leg without its pair` });
    if (p.unwind_escalated) out.push({ key: `escalated-${p.pair_no}`, tone: "bad", text: `Pair ${p.pair_no}: buy-back not filling — CHECK BROKER POSITION` });
    if (p.achieved?.shortfall) out.push({ key: `shortfall-${p.pair_no}`, tone: "bad", text: `Pair ${p.pair_no}: PROTECTION SHORTFALL (charges above the estimate)` });
    if (p.state === "AMBIGUOUS") out.push({ key: `ambiguous-${p.pair_no}`, tone: "bad", text: `Pair ${p.pair_no}: an order's outcome is unknown — waiting for the broker` });
  }
  if (t.exit_failed || t.exit_escalated) out.push({ key: "exit", tone: "bad", text: `Square-off not completing${t.exit_escalated ? `: ${t.exit_escalated}` : ""}` });
  if (bandsLeft > 0) {
    out.push({ key: "band", tone: "warn", text: `Spot is outside the protected band of ${bandsLeft} pair${bandsLeft > 1 ? "s" : ""} (${bandsLeftAuto === 0
      ? "alert only — no automatic exit" : bandsLeftAuto === bandsLeft ? "pair auto-exit is on for each" : "alert only for pairs without auto-exit"})` });
  }
  if (reached.length) out.push({ key: "autoexit-reached", tone: "bad", text: `Pair ${reached.join(", ")}: auto-exit trigger reached — the buy-back starts on the next engine check` });
  if (nearTrigger.length) out.push({ key: "autoexit-near", tone: "bad", text: `Pair ${nearTrigger.join(", ")}: approaching its auto-exit trigger — it will be bought back when reached` });
  if (closing.length) out.push({ key: "autoexit-closing", tone: "warn", text: `Pair ${closing.join(", ")}: being bought back on its own (pair exit)` });
  if (depthShort) out.push({ key: "depth", tone: "warn", text: "P&L is INDICATIVE — not enough visible asks for the open quantity; the profit target is held" });
  return out;
}

// ---- the Futures card structure (PaperTrading CardSection / KV): four titled sections when expanded,
// one compact line when collapsed. Shared by the BUY and SELL options cards (14 Oct 2026).
export function Section({ title, testId, children, stacked }) {
  // `stacked`: one of several sections sharing a column (separated by a top border only)
  const edge = stacked ? "border-t first:border-t-0" : "xl:border-l xl:first:border-l-0 border-t xl:border-t-0 first:border-t-0";
  return (
    <section className={`min-w-0 p-2.5 space-y-1.5 border-[var(--term-border)] ${edge}`} data-testid={testId}>
      <h3 className="text-[10px] font-bold uppercase tracking-wider text-[var(--term-secondary)]">{title}</h3>
      {children}
    </section>
  );
}

export function KV({ label, value, cls, testId, title }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[12px] leading-tight" title={title}>
      <span className="text-[var(--term-secondary)] whitespace-nowrap">{label}</span>
      <span className={`mono font-semibold text-right min-w-0 break-words ${cls || ""}`} data-testid={testId}>{value ?? "—"}</span>
    </div>
  );
}

export function Sections({ children, testId, cols }) {
  return <div className={`grid grid-cols-1 md:grid-cols-2 ${cols || "xl:grid-cols-4"} border border-[var(--term-border)] rounded-sm`} data-testid={testId}>{children}</div>;
}

/** The collapsed card: one compact line of label + value pairs (the Futures card-compact row). */
export function CompactRow({ items, testId = "hedge-summary" }) {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[12px] mono" data-testid={testId}>
      {items.filter(Boolean).map((it, i) => (
        <span key={`${it.label}-${i}`} className={it.right ? "ml-auto" : ""} data-testid={it.testId} title={it.title}>
          <span className="text-[var(--term-secondary)]">{it.label} </span>
          <b className={it.cls || ""}>{it.value ?? "—"}</b>
        </span>))}
    </div>
  );
}

const IST = (epoch) => (epoch ? new Date(Number(epoch) * 1000).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "");
const money = (v) => `₹${Math.abs(Number(v)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signedMoney = (v) => (v === null || v === undefined ? "—" : `${Number(v) >= 0 ? "+" : "−"}${money(v)}`);

/** Peak Profit / Peak Loss (engine-recorded on the trade: options_hedge.peaks - the Futures approach). */
export function PeakLines({ peak, testId = "hedge-peaks" }) {
  const hasMax = peak && peak.max != null && peak.max > 0;
  const hasMin = peak && peak.min != null && peak.min < 0;
  return (
    <div className="grid grid-cols-2 gap-x-3 text-[11px] mono" data-testid={testId}>
      <div title="Highest net P&L to close recorded by the engine on live prices (survives restarts)">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--term-secondary)]">Peak profit</div>
        <div className="text-[#00C805] font-semibold" data-testid={`${testId}-max`}>{hasMax ? signedMoney(peak.max) : "—"}</div>
        <div className="text-[10px] text-[var(--term-muted)]" data-testid={`${testId}-max-at`}>{hasMax ? IST(peak.max_at) : ""}</div>
      </div>
      <div title="Lowest net P&L to close recorded by the engine on live prices (survives restarts)">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--term-secondary)]">Peak loss</div>
        <div className="text-[#FF3B30] font-semibold" data-testid={`${testId}-min`}>{hasMin ? signedMoney(peak.min) : "—"}</div>
        <div className="text-[10px] text-[var(--term-muted)]" data-testid={`${testId}-min-at`}>{hasMin ? IST(peak.min_at) : ""}</div>
      </div>
    </div>
  );
}

/** ECONOMICS, the same for BUY and SELL: net P&L to close (big) vs target, progress, gross and charges,
 *  realised, peak profit / loss, and any extra cells. */
export function EconomicsSection({ netLabel, net, netCls, netTestId, asOf, target, gross, entryCharges, exitCharges,
  realised, peak, held, children, onEditTarget, priceState, bidask, bidaskLabel = "Net P&L at bid/ask", bidaskTestId, grossLabel = "Gross",
  targetCheck }) {
  // targetCheck (SELL, 4 Nov 2026): the target on the engine's VALIDATED LTP basis; without it, as before
  const tc = targetCheck || null;
  const pct = tc ? (tc.usable ? tc.progress_pct : null) : (net != null && target > 0 ? (net / target) * 100 : null);
  const progress = held && pct != null && target > 0 ? Math.max(0, Math.min(100, pct)) : null;
  // the same words as the compact card (lib/marks.progressText): "in loss", never a negative percentage
  const unavailable = held && target > 0 && tc && !tc.usable;
  const progressLabel = held && target > 0 && !unavailable ? progressText(pct, priceState) : null;
  return (
    <Section title="Economics" testId="hedge-sec-economics">
      <div className="flex items-end justify-between gap-3">
        <div title="What closing everything now would realise, after entry charges and estimated exit charges">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--term-secondary)]">{netLabel}</div>
          <div className={`text-xl mono font-bold leading-none ${netCls || ""}`} data-testid={netTestId}>{held ? signedMoney(net) : "—"}</div>
          {bidask !== undefined && <div className="text-[11px] mt-0.5" title="Open legs closed at the bid (SELL to close) / ask (BUY to close) using the visible depth, after entry and estimated exit charges. Reference only - the target compares Net P&L at LTP.">
            <span className="text-[var(--term-muted)]">{bidaskLabel} </span>
            <span className={`mono font-semibold ${bidaskTone(bidask)}`} data-testid={bidaskTestId}>{held ? signedMoney(bidask) : "—"}</span></div>}
          {asOf}
        </div>
        <button type="button" className="text-right rounded-sm px-1 hover:bg-[var(--term-hover)] disabled:hover:bg-transparent" onClick={onEditTarget}
          disabled={!onEditTarget} data-testid="hedge-econ-target" title="Profit target (auto-exit compares the net P&L to close with it, on live prices)">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--term-secondary)]">Target{onEditTarget ? " ✎" : ""}</div>
          <div className="text-sm mono font-bold leading-none">{target ? money(target) : "off"}</div>
        </button>
      </div>
      {progressLabel != null && <div className="flex justify-between text-[11px]" data-testid="hedge-econ-progress">
        <span className="text-[var(--term-secondary)]">Progress to target</span>
        <b className={`mono ${progressLabel === "in loss" ? "text-[#FF3B30]" : progressLabel === "—" ? "text-[var(--term-muted)]" : ""}`}
          title={priceState === "MARKET_CLOSED" ? "Not checked while the market is closed" : undefined}>{progressLabel}</b></div>}
      {unavailable && <div className="rounded-sm border border-[#B45309]/50 bg-[#B45309]/10 px-2 py-1 text-[11px]" data-testid="hedge-econ-target-unavailable">
        <b className="text-[#B45309]">Target check unavailable — stale/unusable LTP</b>
        <div className="text-[var(--term-secondary)]">The Net P&amp;L at LTP above is indicative only and is not used for the auto-exit decision.</div>
        {(tc.refused || []).length > 0 && <ul className="mono mt-0.5" data-testid="hedge-econ-refused-legs">
          {tc.refused.map((r) => <li key={r.token}>{r.symbol || r.token}: LTP {r.ltp ?? "—"} · bid {r.bid ?? "—"} · ask {r.ask ?? "—"}{r.trade_age_s != null ? ` · last trade ${Math.round(r.trade_age_s)} s ago` : ""}</li>)}
        </ul>}</div>}
      {progress != null && priceState !== "MARKET_CLOSED" && (
        <div className="h-1.5 rounded bg-[var(--term-s3)] overflow-hidden" role="progressbar" aria-label="Net P&L towards target"
          aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100}>
          <div className={`h-full ${progress >= 100 ? "bg-[#00C805]" : "bg-[#2563EB]"}`} style={{ width: `${progress}%` }} />
        </div>
      )}
      {held && <div className="text-[11px] mono text-[var(--term-secondary)]" data-testid="hedge-charges-line">
        {grossLabel} {signedMoney(gross)} · entry charges {entryCharges == null ? "—" : money(entryCharges)} · ~exit {exitCharges == null ? "—" : money(exitCharges)}</div>}
      <KV label="Realised" value={signedMoney(realised)} cls={realised > 0 ? "text-[#00C805]" : realised < 0 ? "text-[#FF3B30]" : ""} testId="hedge-stat-realized" />
      <PeakLines peak={peak} />
      {children}
    </Section>
  );
}
